<template>
  <TransitionRoot
    as="template"
    :show="open"
  >
    <Dialog
      as="div"
      class="fixed inset-0 overflow-hidden z-50"
      @close="emit('close')"
    >
      <div class="absolute inset-0 overflow-hidden">
        <TransitionChild
          as="template"
          enter="ease-in-out duration-500"
          enter-from="opacity-0"
          enter-to="opacity-60"
          leave="ease-in-out duration-500"
          leave-from="opacity-60"
          leave-to="opacity-0"
        >
          <DialogOverlay class="absolute inset-0" />
        </TransitionChild>
        <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 mt-24">
          <TransitionChild
            as="template"
            enter="transition ease-in-out duration-500 sm:duration-700"
            enter-from="translate-x-full"
            enter-to="translate-x-0"
            leave="transition ease-in-out duration-500 sm:duration-700"
            leave-from="translate-x-0"
            leave-to="translate-x-full"
          >
            <div class="pointer-events-auto relative w-screen max-w-md">
              <!-- START TRANSFER -->
              <div
                class="flex h-full flex-col pt-6 overflow-y-scroll bg-gray-100 pb-16 rounded-tl-2xl shadow-xl border border-gray-400"
              >
                <div class="px-4 sm:px-6 flex justify-between items-center">
                  <DialogTitle
                    class="text-lg font-semibold flex flex-col"
                    :class="{
                      'text-red-400': Boolean(selectedTransfer.flags?.length),
                      'text-gray-800': !Boolean(selectedTransfer.flags?.length)
                    }"
                  >
                    Transfer: {{ selectedTransfer.reference_id }}
                    <span
                      class="text-base text-gray-600 leading-none"
                      v-if="selectedTransfer.type === 'Return'"
                    >
                      Returning from {{ selectedTransfer.parent?.reference_id }}
                    </span>
                  </DialogTitle>

                  <div class="flex items-center gap-2">
                    <Menu
                      as="div"
                      class="relative text-right inline-block"
                    >
                      <div>
                        <MenuButton
                          class="inline-flex items-center justify-center w-full rounded-full border border-gray-400 px-3 py-1 uppercase text-xs font-semibold text-gray-800 h-8 px-4"
                        >
                          {{ selectedTransfer.display_status }}
                          <ChevronDownIcon
                            class="-mr-1 ml-2 h-4 w-4 text-gray-800"
                            aria-hidden="true"
                          />
                        </MenuButton>
                      </div>
                      <transition
                        enter-active-class="transition ease-out duration-100"
                        enter-from-class="opacity-0 scale-95"
                        enter-to-class="opacity-100 scale-100"
                        leave-active-class="transition ease-in duration-75"
                        leave-from-class="opacity-100 scale-100"
                        leave-to-class="opacity-0 scale-95"
                      >
                        <MenuItems
                          class="justify-end origin-top-right absolute right-0 mt-2 w-32 rounded-md shadow-lg bg-gray-100 divide-y divide-gray-400 focus:outline-none z-50"
                        >
                          <div class="py-1">
                            <MenuItem
                              v-if="canEdit"
                              v-slot="{ active }"
                            >
                              <a
                                href="#"
                                @click.prevent="emit('openEdit', selectedTransfer)"
                                :class="[
                                  active ? 'bg-gray-400 text-gray-900' : 'text-gray-700',
                                  'group flex items-center px-4 py-2 text-xs font-semibold hover:bg-blue-600 hover:text-gray-100'
                                ]"
                              >
                                Edit
                              </a>
                            </MenuItem>
                            <MenuItem
                              v-if="canPrepare"
                              v-slot="{ active }"
                            >
                              <a
                                href="#"
                                @click.prevent="updatePrepared"
                                :class="[
                                  active ? 'bg-gray-400 text-gray-900' : 'text-gray-700',
                                  'group flex items-center px-4 py-2 text-xs font-semibold hover:bg-blue-600 hover:text-gray-100'
                                ]"
                              >
                                Lock
                              </a>
                            </MenuItem>
                            <MenuItem
                              v-if="canProcess"
                              v-slot="{ active }"
                            >
                              <a
                                href="#"
                                @click.prevent="updateInProcessing"
                                :class="[
                                  active ? 'bg-gray-400 text-gray-900' : 'text-gray-700',
                                  'group flex items-center px-4 py-2 text-xs font-semibold hover:bg-blue-600 hover:text-gray-100'
                                ]"
                              >
                                Processing
                              </a>
                            </MenuItem>
                            <MenuItem
                              v-if="canSend"
                              v-slot="{ active }"
                            >
                              <a
                                href="#"
                                @click.prevent="updateInTransit"
                                :class="[
                                  active ? 'bg-gray-400 text-gray-900' : 'text-gray-700',
                                  'group flex items-center px-4 py-2 text-xs font-semibold hover:bg-blue-600 hover:text-gray-100'
                                ]"
                              >
                                In Transit
                              </a>
                            </MenuItem>
                            <MenuItem
                              v-if="canSetDrivers"
                              v-slot="{ active }"
                            >
                              <a
                                href="#"
                                @click.prevent="emit('setDrivers')"
                                :class="[
                                  active ? 'bg-gray-400 text-gray-900' : 'text-gray-700',
                                  'group flex items-center px-4 py-2 text-xs font-semibold hover:bg-blue-600 hover:text-gray-100'
                                ]"
                              >
                                Set Drivers
                              </a>
                            </MenuItem>
                            <MenuItem
                              v-if="canSetVehicle"
                              v-slot="{ active }"
                            >
                              <a
                                href="#"
                                @click.prevent="emit('setVehicle')"
                                :class="[
                                  active ? 'bg-gray-400 text-gray-900' : 'text-gray-700',
                                  'group flex items-center px-4 py-2 text-xs font-semibold hover:bg-blue-600 hover:text-gray-100'
                                ]"
                              >
                                Set Vehicle
                              </a>
                            </MenuItem>
                            <MenuItem
                              v-if="canReceive"
                              v-slot="{ active }"
                            >
                              <a
                                href="#"
                                @click.prevent="emit('openReceive')"
                                :class="[
                                  active ? 'bg-gray-400 text-gray-900' : 'text-gray-700',
                                  'group flex items-center px-4 py-2 text-xs font-semibold hover:bg-blue-600 hover:text-gray-100'
                                ]"
                              >
                                Receive
                              </a>
                            </MenuItem>
                            <MenuItem
                              v-if="canPrintLabels"
                              v-slot="{ active }"
                            >
                              <a
                                href="#"
                                @click.prevent="emit('openPrintLabels')"
                                :class="[
                                  active ? 'bg-gray-400 text-gray-900' : 'text-gray-700',
                                  'group flex items-center px-4 py-2 text-xs font-semibold hover:bg-blue-600 hover:text-gray-100'
                                ]"
                              >
                                Print Labels
                              </a>
                            </MenuItem>
                            <MenuItem v-slot="{ active }">
                              <a
                                href="#"
                                @click.prevent="showManifest"
                                :class="[
                                  active ? 'bg-gray-400 text-gray-900' : 'text-gray-700',
                                  'group flex items-center px-4 py-2 text-xs font-semibold hover:bg-blue-600 hover:text-gray-100'
                                ]"
                              >
                                View Manifest
                              </a>
                            </MenuItem>
                            <MenuItem v-slot="{ active }">
                              <a
                                href="#"
                                @click.prevent="showActivityLog"
                                :class="[
                                  active ? 'bg-gray-400 text-gray-900' : 'text-gray-700',
                                  'group flex items-center px-4 py-2 text-xs font-semibold hover:bg-blue-600 hover:text-gray-100'
                                ]"
                              >
                                Activity Log
                              </a>
                            </MenuItem>
                            <div
                              v-if="canCancel"
                              class="border-t border-gray-400"
                            >
                              <MenuItem v-slot="{ active }">
                                <a
                                  href="#"
                                  @click.prevent="updateCancel"
                                  :class="[
                                    active ? 'bg-gray-400 text-gray-900' : 'text-gray-700',
                                    'group flex items-center px-4 py-2 text-xs font-semibold hover:bg-blue-600 hover:text-gray-100'
                                  ]"
                                >
                                  Cancel
                                </a>
                              </MenuItem>
                            </div>
                            <div
                              v-if="isAdmin"
                              class="border-t border-gray-400"
                            >
                              <MenuItem v-slot="{ active }">
                                <a
                                  :href="
                                    route('nova.pages.detail', {
                                      resource: 'transfers',
                                      resourceId: selectedTransfer.id
                                    })
                                  "
                                  target="_blank"
                                  :class="[
                                    active ? 'bg-gray-400 text-gray-900' : 'text-gray-700',
                                    'group flex items-center px-4 py-2 text-xs font-semibold hover:bg-blue-600 hover:text-gray-100'
                                  ]"
                                >
                                  Open in Nova
                                </a>
                              </MenuItem>
                            </div>
                          </div>
                        </MenuItems>
                      </transition>
                    </Menu>

                    <a
                      @click.prevent="emit('openEdit', selectedTransfer)"
                      v-if="canEdit"
                      href="#"
                      class="rounded-full w-8 h-8 border border-gray-400 flex items-center justify-center"
                    >
                      <PencilIcon class="w-4 h-4 text-blue-600" />
                    </a>
                  </div>
                </div>

                <FlagStatus
                  :flags="selectedTransfer.flags"
                  class="px-4 sm:px-6 mt-3"
                />

                <!-- START Details -->
                <div class="relative flex flex-col flex-1 pt-8">
                  <div class="flow-root px-4 sm:px-6">
                    <ul
                      role="list"
                      class="-mb-8"
                    >
                      <li>
                        <div class="relative pb-8">
                          <span
                            class="absolute top-6 left-4 -ml-px h-full w-0.5 z-0 border border-gray-400 border-dashed"
                            aria-hidden="true"
                          />
                          <div class="relative flex space-x-3 z-10">
                            <div>
                              <ArrowDownCircleIcon
                                class="h-8 w-8 rounded-full flex items-center justify-center text-gray-800 bg-gray-100"
                                aria-hidden="true"
                              />
                            </div>
                            <div class="min-w-0 flex-1 flex justify-between space-x-4">
                              <div>
                                <p class="font-semibold p-0 m-0 leading-tight text-base">
                                  {{ selectedTransfer.origin?.name || defaultLocationName }}
                                </p>
                                <span
                                  v-if="selectedTransfer.origin_space_id"
                                  class="text-gray-700 font-medium text-xs p-0 m-0 block"
                                >
                                  {{ selectedTransfer.origin_space.name }}
                                </span>
                                <span
                                  v-if="selectedTransfer.transferred_at"
                                  class="text-gray-700 font-medium text-xs p-0 m-0 block"
                                >
                                  {{ _datetime(selectedTransfer.transferred_at) }}
                                  <span v-if="selectedTransfer.transferrer">
                                    <br />
                                    {{
                                      selectedTransfer.transferrer.name || selectedTransfer.transferrer.email || 'N/A'
                                    }}
                                  </span>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div class="relative pb-8">
                          <span
                            class="absolute top-6 left-4 -ml-px h-full w-0.5 z-0 border border-gray-400 border-dashed"
                            aria-hidden="true"
                          />
                          <div class="relative flex space-x-3 z-10">
                            <div>
                              <ArrowDownCircleIcon
                                class="h-8 w-8 rounded-full flex items-center justify-center bg-gray-100"
                                :class="{
                                  'text-gray-800': selectedTransfer.sent_at,
                                  'text-gray-500': !selectedTransfer.sent_at
                                }"
                                aria-hidden="true"
                              />
                            </div>
                            <div class="min-w-0 flex-1 flex justify-between space-x-4">
                              <div>
                                <p class="font-semibold p-0 m-0 leading-tight text-base">In Transit</p>
                                <span
                                  v-if="selectedTransfer.sent_at"
                                  class="text-gray-700 text-xs p-0 m-0 block"
                                >
                                  {{ _datetime(selectedTransfer.sent_at) }}
                                  <span v-if="selectedTransfer.sender">
                                    <br />
                                    {{ selectedTransfer.sender.name || selectedTransfer.sender.email || 'N/A' }}
                                  </span>
                                </span>
                                <span
                                  v-for="(driver, index) in selectedTransfer.drivers"
                                  class="text-gray-700 font-medium text-xs p-0 m-0 block"
                                >
                                  Driver #{{ index + 1 }}: {{ driver.display }}
                                </span>
                                <span
                                  v-if="selectedTransfer.vehicle"
                                  class="text-gray-700 font-medium text-xs p-0 m-0 block"
                                >
                                  Vehicle: {{ selectedTransfer.vehicle.name }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div class="relative pb-8">
                          <div class="relative flex space-x-3 z-10">
                            <div>
                              <ArrowDownCircleIcon
                                class="h-8 w-8 rounded-full flex items-center justify-center bg-gray-100"
                                :class="{
                                  'text-gray-800': selectedTransfer.received_at,
                                  'text-gray-500': !selectedTransfer.received_at
                                }"
                                aria-hidden="true"
                              />
                            </div>
                            <div class="min-w-0 flex-1 flex justify-between space-x-4">
                              <div>
                                <p class="font-semibold p-0 m-0 leading-tight text-base">
                                  {{ selectedTransfer.destination?.name || defaultLocationName }}
                                </p>
                                <span
                                  v-if="selectedTransfer.destination_space_id"
                                  class="text-gray-700 font-medium text-xs p-0 m-0 block"
                                >
                                  {{ selectedTransfer.destination_space.name }}
                                </span>
                                <span
                                  v-if="selectedTransfer.received_at"
                                  class="text-gray-700 font-medium text-xs p-0 m-0 block"
                                >
                                  {{ _datetime(selectedTransfer.received_at) }}
                                  <span v-if="selectedTransfer.receiver">
                                    <br />
                                    {{ selectedTransfer.receiver.name || selectedTransfer.receiver.email || 'N/A' }}
                                  </span>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>

                  <div
                    v-if="canEdit"
                    class="border-t border-gray-400 bg-gray-200 flex-1 mt-8"
                  >
                    <div class="divide-y divide-gray-400">
                      <div class="grid grid-cols-6 gap-3 py-3 px-8">
                        <div
                          class="col-span-4 text-left text-xs uppercase text-gray-800 font-semibold"
                          :class="[hasDemandTransferItems ? 'col-span-4' : 'col-span-5']"
                        >
                          <span>Package</span>
                        </div>
                        <div class="col-span-1 text-center text-xs uppercase text-gray-800 font-semibold">
                          <span>Count</span>
                        </div>
                        <div
                          v-if="hasDemandTransferItems"
                          class="col-span-1 text-center text-xs uppercase text-gray-800 font-semibold"
                        >
                          <span>Demand</span>
                        </div>
                      </div>

                      <div
                        v-for="item in selectedTransfer.packages"
                        class="px-8"
                        :class="{ 'bg-red-200': !!getErrors('package_' + item.parent_id + '_count') }"
                      >
                        <div class="grid grid-cols-6 gap-3 py-3 items-center">
                          <div
                            class="text-left flex flex-col leading-tight"
                            :class="[hasDemandTransferItems ? 'col-span-4' : 'col-span-5']"
                          >
                            <FlagStatus
                              :flags="item.flags"
                              class="pb-1"
                            />
                            <Link
                              :href="showPackageRoute(item)"
                              class="uppercase font-semibold text-sm text-ellipsis whitespace-nowrap overflow-hidden flex items-center gap-1"
                              :class="{
                                'text-red-400 hover:text-red-400': Boolean(item.flags?.length)
                              }"
                            >
                              {{ item.product_name }}
                            </Link>
                            <span class="text-gray-800 text-sm font-medium flex flex-col justify-center gap-x-2">
                              <template v-if="isStandardTransfer">
                                {{ item.details.tag_num }}
                              </template>
                              <template v-else>
                                {{ item.parent_sub_lot_num }}
                                <span class="flex items-center gap-x-2">
                                  <ArrowDownOnSquareStackIcon class="w-6 h-6 text-gray-800 -rotate-90" />
                                  {{ item.sub_lot_num }}
                                </span>
                              </template>
                            </span>
                            <div
                              v-if="getErrors('package_' + item.parent_id + '_count')"
                              class="text-red-600 text-xs font-semibold"
                            >
                              {{ getErrors('package_' + item.parent_id + '_count') }}
                            </div>
                          </div>
                          <div
                            class="col-span-1 bg-gray-100 select-none rounded-lg border-2 border-gray-400 h-[36px] flex items-center justify-center text-sm uppercase font-semibold text-gray-800"
                          >
                            <div :class="getErrors('package_' + item.parent_id + '_count') ? 'border-red-600' : ''">
                              {{ item.quantity }}
                            </div>
                          </div>
                          <div
                            v-if="item.quantity_pre_reserved"
                            class="col-span-1 bg-gray-100 select-none rounded-lg border-2 border-gray-400 h-[36px] flex items-center justify-center text-sm uppercase font-semibold text-gray-800"
                          >
                            <div class="">{{ item.quantity_pre_reserved }}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      class="bg-gray-200 grid grid-cols-5 gap-5 h-16 px-4 sm:px-6 items-center border-t border-gray-400 fixed bottom-0 w-full max-w-md"
                    >
                      <div class="col-span-4 text-left flex flex-col leading-tight">
                        <span class="text-gray-800 font-semibold">Totals</span>
                      </div>
                      <div class="col-span-1 text-center uppercase text-blue-600 font-semibold">
                        <div
                          class="rounded-lg w-full bg-gray-100 text-sm px-3 py-1 font-semibold text-center border-2 border-gray-300"
                        >
                          {{ selectedTransfer.packages_sum_quantity ?? '0' }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="!canEdit"
                    class="border-t border-gray-400 bg-gray-200 flex-1 mt-8"
                  >
                    <div class="divide-y divide-gray-400">
                      <div class="grid grid-cols-6 gap-3 py-3 px-8">
                        <div class="col-span-4 text-left text-xs uppercase text-gray-800 font-semibold">
                          <span>Package</span>
                        </div>
                        <div class="col-span-1 text-center text-xs uppercase text-gray-800 font-semibold">
                          <span>Count</span>
                        </div>
                        <div class="col-span-1 text-center text-xs uppercase text-gray-800 font-semibold">
                          <span>Actual</span>
                        </div>
                      </div>

                      <div
                        v-for="item in selectedTransfer.packages"
                        class="px-8"
                        :class="{ 'bg-red-200': !!getErrors('package_' + item.parent_id + '_count') }"
                      >
                        <div class="grid grid-cols-6 gap-3 py-3 items-center">
                          <div class="col-span-4 text-left flex flex-col leading-tight">
                            <FlagStatus
                              :flags="item.flags"
                              class="pb-1"
                            />
                            <Link
                              :href="showPackageRoute(item)"
                              class="uppercase font-semibold text-sm text-ellipsis whitespace-nowrap overflow-hidden"
                              :class="{
                                'text-red-400 hover:text-red-400': hasAdjustments(item) || Boolean(item.flags?.length)
                              }"
                            >
                              {{ item.product_name }}
                            </Link>
                            <span class="text-gray-800 text-sm font-medium flex flex-col justify-center gap-x-2">
                              <template v-if="isStandardTransfer">
                                {{ item.details.tag_num }}
                              </template>
                              <template v-else>
                                {{ item.parent_sub_lot_num }}
                                <span
                                  class="flex items-center gap-x-2"
                                  :class="{
                                    'text-green-600': isNewSubLot(item),
                                    'text-gray-800': !isNewSubLot(item),
                                    'font-bold': isNewSubLot(item)
                                  }"
                                >
                                  <ArrowDownOnSquareStackIcon class="w-6 h-6 -rotate-90" />
                                  {{ item.sub_lot_num }}
                                </span>
                              </template>
                            </span>
                            <div
                              v-if="getErrors('package_' + item.parent_id + '_count')"
                              class="text-red-600 text-xs font-semibold"
                            >
                              {{ getErrors('package_' + item.parent_id + '_count') }}
                            </div>
                          </div>
                          <div
                            class="col-span-1 bg-gray-100 select-none rounded-lg border-2 border-gray-400 h-[36px] flex items-center justify-center text-sm uppercase font-semibold text-gray-800"
                            :class="{ 'text-red-600': hasAdjustments(item), 'text-blue-600': !hasAdjustments(item) }"
                          >
                            <span>
                              {{ item.transfer_quantity_adjusted }}
                            </span>
                          </div>
                          <div
                            class="col-span-1 bg-gray-100 select-none rounded-lg border-2 border-gray-400 h-[36px] flex items-center justify-center text-sm uppercase font-semibold text-gray-800"
                            :class="{ 'text-red-600': hasAdjustments(item), 'text-blue-600': !hasAdjustments(item) }"
                          >
                            <span>
                              {{ item.quantity }}
                            </span>
                          </div>
                        </div>

                        <div
                          v-if="hasAdjustments(item)"
                          class="pb-3 border-none"
                        >
                          <div
                            v-for="adjustment in item.adjustments"
                            class="grid grid-cols-5 gap-5 items-center divide-none border-0 border-none px-4 sm:px-6"
                          >
                            <div class="col-span-4 text-sm flex items-center gap-2 text-red-600">
                              {{ adjustment.reason }}
                            </div>
                            <div class="col-span-1 text-center text-sm uppercase text-red-600 font-semibold">
                              {{ adjustment.amount }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      class="grid grid-cols-6 gap-3 h-16 px-4 sm:px-6 items-center border-t border-gray-400 bg-gray-300 fixed bottom-0 w-full max-w-md"
                    >
                      <div class="col-span-4 text-left flex flex-col leading-tight">
                        <span class="text-gray-800 font-semibold">Totals</span>
                      </div>
                      <div
                        class="col-span-1 bg-gray-100 select-none rounded-lg border-2 border-gray-400 h-[36px] flex items-center justify-center text-sm uppercase font-semibold text-gray-800"
                      >
                        <span>
                          {{ selectedTransfer.packages_sum_quantity_adjusted }}
                        </span>
                      </div>
                      <div
                        v-if="selectedTransfer.packages_sum_quantity"
                        class="col-span-1 bg-gray-100 select-none rounded-lg border-2 border-gray-400 h-[36px] flex items-center justify-center text-sm uppercase font-semibold text-gray-800"
                      >
                        <span>
                          {{ selectedTransfer.packages_sum_quantity }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- END Details -->
              </div>
              <!-- END TRANSFER -->
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ArrowDownCircleIcon, PencilIcon, ArrowDownOnSquareStackIcon } from '@heroicons-2/vue/24/outline'
import { ChevronDownIcon } from '@heroicons/vue/solid'
import { Link, useForm, usePage } from '@inertiajs/vue3'
import {
  Dialog,
  DialogOverlay,
  DialogTitle,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue'
import { computed } from 'vue'
import _datetime from '@/Filters/DateTime'
import _ from 'lodash'
import FlagStatus from '@/Components/Flags/FlagStatus.vue'
import { ROLE_ADMIN } from '@/utils/roles'
import { useConfirmDialog } from '@/Composables/confirm-dialog'
import { settle } from '@/utils/promises'

// Props
const props = defineProps({
  open: Boolean,
  selectedTransfer: Object,
  defaultLocationName: String,
  errors: Object
})

const page = usePage()

// Emits
const emit = defineEmits(['close', 'setDrivers', 'setVehicle', 'openReceive', 'openEdit', 'openPrintLabels'])

// Data

// Computed
const isStandardTransfer = computed(() => {
  return props.selectedTransfer?.method === 'standard'
})

const hasDemandTransferItems = computed(() => {
  return (
    props.selectedTransfer &&
    props.selectedTransfer.packages.reduce((acc, item) => {
      return acc || item.quantity_pre_reserved
    }, false)
  )
})

const canEdit = computed(() => {
  return props.selectedTransfer?.status === 'pending'
})
const canPrepare = computed(() => {
  return props.selectedTransfer?.status === 'pending'
})
const canProcess = computed(() => {
  return props.selectedTransfer?.status === 'prepared'
})
const canSend = computed(() => {
  return props.selectedTransfer?.status === 'processed'
})

const canSetDrivers = computed(() => {
  return (
    props.selectedTransfer &&
    props.selectedTransfer?.status !== 'cancelled' &&
    props.selectedTransfer?.status !== 'received'
  )
})

const canSetVehicle = computed(() => {
  return (
    props.selectedTransfer &&
    props.selectedTransfer?.status !== 'cancelled' &&
    props.selectedTransfer?.status !== 'received'
  )
})

const canReceive = computed(() => {
  return props.selectedTransfer?.status === 'in_transit'
})

const canPrintLabels = computed(() => {
  return props.selectedTransfer?.status !== 'pending' && props.selectedTransfer?.status !== 'cancelled'
})

const canCancel = computed(() => {
  return (
    props.selectedTransfer &&
    props.selectedTransfer?.status !== 'cancelled' &&
    props.selectedTransfer?.status !== 'received' &&
    props.selectedTransfer?.type !== 'Return'
  )
})

const isAdmin = computed(() => {
  return page.props.auth?.user?.system_role === ROLE_ADMIN
})

const isNewSubLot = (pkg) => {
  return pkg.parent_sub_lot_num !== pkg.sub_lot_num
}

// Methods
const showPackageRoute = (pkg) => {
  return route('packages.show', { package: pkg.id })
}

const updateInTransit = async () => {
  if (
    !window.confirm(
      'Are you sure this transfer is in-transit? Note: Transfers do not need to be marked in-transit to remove on-hand inventory'
    )
  ) {
    return
  }

  const form = useForm({})

  form.put(route('transfers.send', { transfer: props.selectedTransfer.id }), {
    errorBag: 'updateTransfer',
    onSuccess: (response) => {},
    onFinish: (response) => {}
  })
}

const updatePrepared = () => {
  if (
    !window.confirm(
      'Locking the transfer will remove on-hand inventory and make the transfer un-editable. Are you sure?'
    )
  ) {
    return
  }

  const form = useForm({})

  form.put(route('transfers.prepare', { transfer: props.selectedTransfer.id }), {
    errorBag: 'updateTransfer',
    onSuccess: (response) => {},
    onFinish: (response) => {}
  })
}

const updateInProcessing = () => {
  const form = useForm({})

  form.put(route('transfers.process', { transfer: props.selectedTransfer.id }), {
    errorBag: 'updateTransfer',
    onSuccess: (response) => {},
    onFinish: (response) => {}
  })
}

const updateCancel = () => {
  if (!window.confirm('Are you sure you want to cancel?')) {
    return
  }
  const form = useForm({})

  form.put(route('transfers.cancel', { transfer: props.selectedTransfer.id }), {
    errorBag: 'updateTransfer',
    onSuccess: (response) => {},
    onFinish: (response) => {}
  })
}

const showManifest = () => {
  window.open(route('transfers.manifest.show', { transfer: props.selectedTransfer.id }), '_blank')
}

const showActivityLog = () => {
  window.open(
    route('activity-log', { resource_type: 'transfer', resource_id: props.selectedTransfer.id, status: 'all' }),
    '_blank'
  )
}

const hasAdjustments = (pkg) => {
  return !_.isEmpty(pkg.adjustments || [])
}

const getErrors = (key) => {
  return !_.isEmpty(props.errors.updateTransfer) ? props.errors.updateTransfer[key] : null
}
</script>
